/**
 * animal-animator.js
 * Bộ điều khiển cử động (Animation Controller) cho động vật Voxel
 * Hỗ trợ cả 2 khung giải phẫu học: Động vật 4 chân (Quadruped) và Gia cầm 2 chân (Avian).
 * Xử lý nội suy mượt mà (lerp) giữa các tư thế sinh học.
 */

import { easeInOutQuad, lerp, lerpPose } from '../core/easing.js';

export const ANIMAL_ACTIONS = {
  quadruped: ['idle', 'walk', 'run', 'sit', 'sleep', 'jump', 'eat'],
  avian: ['idle', 'walk', 'run', 'peck', 'eat', 'flap', 'sit', 'sleep']
};

export class AnimalAnimator {
  /**
   * @param {object} partsMap - Bản đồ các bộ phận mesh { body, neck, head, tail, legs, wings... }
   * @param {string} archetype - 'quadruped' | 'avian'
   * @param {object} options - Tùy chọn bổ sung (legHeight, etc.)
   */
  constructor(partsMap, archetype = 'quadruped', options = {}) {
    this.parts = partsMap;
    this.archetype = archetype;
    this.legHeight = options.legHeight || 0.16;
    this.woolThickness = options.woolThickness || 0;
    this.currentAction = 'idle';
    this.targetAction = 'idle';
    this.transitionProgress = 1.0;
    this.transitionSpeed = 4.5;
    this.time = 0;
    this.actionElapsed = 0;

    this.baseBodyY = partsMap.body && partsMap.body.position ? partsMap.body.position.y : 0;
  }

  createEmptyPose() {
    return {
      bodyY: 0,
      bodyRotX: 0,
      bodyRotY: 0,
      bodyRotZ: 0,
      neckRotX: 0,
      neckRotY: 0,
      neckRotZ: 0,
      headRotX: 0,
      headRotY: 0,
      headRotZ: 0,
      headPosZ: 0,
      tailRotX: 0,
      tailRotY: 0,
      tailRotZ: 0,
      // 4 Chân (Quadruped)
      flLegRotX: 0,
      frLegRotX: 0,
      blLegRotX: 0,
      brLegRotX: 0,
      // 2 Chân & 2 Cánh (Avian)
      leftLegRotX: 0,
      rightLegRotX: 0,
      leftWingRotZ: 0,
      rightWingRotZ: 0,
      // Tai (Ears)
      leftEarRotZ: 0,
      rightEarRotZ: 0
    };
  }

  play(actionName) {
    const allowed = ANIMAL_ACTIONS[this.archetype] || ANIMAL_ACTIONS.quadruped;
    let target = actionName;

    // Fallback thông minh: Cho phép 'eat' cho avian ánh xạ sang 'peck'
    if (this.archetype === 'avian' && target === 'eat') {
      target = 'peck';
    }

    if (!allowed.includes(target)) {
      console.warn(
        `[AnimalAnimator] Action '${actionName}' not supported for archetype '${this.archetype}'. Fallback to 'idle'.`
      );
      target = 'idle';
    }

    if (this.currentAction === target && this.targetAction === target) {
      return;
    }
    this.actionElapsed = 0;
    this.targetAction = target;
    this.transitionProgress = 0.0;
  }

  easeInOutQuad(x) {
    return easeInOutQuad(x);
  }

  lerp(a, b, t) {
    return lerp(a, b, t);
  }

  lerpPose(from, to, t) {
    return lerpPose(from, to, t);
  }

  update(delta) {
    this.time += delta;
    this.actionElapsed += delta;

    if (this.transitionProgress < 1.0) {
      this.transitionProgress = Math.min(1.0, this.transitionProgress + delta * this.transitionSpeed);
      if (this.transitionProgress >= 1.0) {
        this.currentAction = this.targetAction;
      }
    }

    const poseFrom = this.computePose(this.currentAction, this.time, this.actionElapsed);
    const poseTo = this.computePose(this.targetAction, this.time, this.actionElapsed);

    const t = this.easeInOutQuad(this.transitionProgress);
    const interpolated = this.lerpPose(poseFrom, poseTo, t);

    this.applyPose(interpolated);
  }

  computePose(action, time, elapsed) {
    return this.archetype === 'avian'
      ? this.computeAvianPose(action, time, elapsed)
      : this.computeQuadrupedPose(action, time, elapsed);
  }

  // ==========================================
  // I. CHUYỂN ĐỘNG ĐỘNG VẬT 4 CHÂN (QUADRUPED)
  // ==========================================
  computeQuadrupedPose(action, time, elapsed) {
    const pose = this.createEmptyPose();

    switch (action) {
      case 'idle': {
        // Nhịp thở nhấp nhô nhẹ, đầu ngó nghiêng, đuôi phe phẩy
        pose.bodyY = Math.sin(time * 2.0) * 0.006;
        pose.headRotY = Math.sin(time * 0.8) * 0.08;
        pose.headRotX = Math.sin(time * 1.5) * 0.03;
        pose.tailRotY = Math.sin(time * 3.5) * 0.28;
        pose.leftEarRotZ = Math.sin(time * 2.8) * 0.04;
        pose.rightEarRotZ = -Math.sin(time * 2.8) * 0.04;
        break;
      }

      case 'walk': {
        // Dáng đi lạch bạch nhún nhảy (Bouncy Waddle) chân ngắn
        const phase = time * 7.5;
        pose.flLegRotX = Math.sin(phase) * 0.35;
        pose.brLegRotX = Math.sin(phase) * 0.35;
        pose.frLegRotX = -Math.sin(phase) * 0.35;
        pose.blLegRotX = -Math.sin(phase) * 0.35;

        pose.bodyY = Math.abs(Math.sin(phase)) * 0.015;
        pose.bodyRotZ = Math.sin(phase) * 0.04;
        pose.headRotX = Math.sin(phase) * 0.05;
        pose.headRotY = Math.sin(phase * 0.5) * 0.04;
        pose.tailRotY = Math.sin(phase * 1.2) * 0.25;
        break;
      }

      case 'run': {
        // Nhịp phi nước kiệu nhanh nhún nhảy
        const phase = time * 13.0;
        pose.flLegRotX = Math.sin(phase) * 0.65;
        pose.brLegRotX = Math.sin(phase) * 0.6;
        pose.frLegRotX = -Math.sin(phase) * 0.65;
        pose.blLegRotX = -Math.sin(phase) * 0.6;

        pose.bodyY = Math.sin(phase) * 0.025;
        pose.bodyRotX = Math.sin(phase) * 0.06;
        pose.headRotX = Math.sin(phase) * 0.07;
        pose.tailRotX = -0.2;
        pose.tailRotY = Math.sin(phase * 1.5) * 0.35;
        break;
      }

      case 'eat': {
        // Cúi sát đất gặm cỏ (đầu trực tiếp chúc xuống sàn)
        pose.headRotX = 0.75 + Math.sin(time * 3.5) * 0.06;
        pose.bodyRotX = 0.06;
        pose.tailRotY = Math.sin(time * 2.5) * 0.18;
        break;
      }

      case 'sit': {
        // Ngồi xổm sát sàn (thân hạ thấp theo tỉ lệ chân, đầu ngước lên quan sát, bù trừ len chống lún sàn N-2)
        const woolOffset = this.woolThickness ? this.woolThickness * 0.8 : 0;
        pose.bodyY = -this.legHeight * 0.65 + woolOffset;
        pose.bodyRotX = this.woolThickness ? -0.16 : -0.22;
        pose.headRotX = 0.2 + Math.sin(time * 1.5) * 0.03;
        pose.flLegRotX = 0.25;
        pose.frLegRotX = 0.25;
        pose.blLegRotX = -0.85;
        pose.brLegRotX = -0.85;
        pose.tailRotX = 0.15;
        pose.tailRotY = Math.sin(time * 2.2) * 0.2;
        break;
      }

      case 'sleep': {
        // Nằm sát sàn theo tỉ lệ chân
        pose.bodyY = -this.legHeight * 0.85;
        pose.headRotX = 0.3;
        pose.headRotZ = 0.15;
        pose.flLegRotX = 0.45;
        pose.frLegRotX = 0.45;
        pose.blLegRotX = -0.65;
        pose.brLegRotX = -0.65;
        break;
      }

      case 'jump': {
        // Bật nhảy vui vẻ
        const cycle = (elapsed * 2.5) % 1.0;
        const jumpH = Math.sin(cycle * Math.PI) * 0.12;
        pose.bodyY = jumpH;
        pose.bodyRotX = -Math.sin(cycle * Math.PI) * 0.15;
        pose.flLegRotX = -0.35;
        pose.frLegRotX = -0.35;
        pose.blLegRotX = 0.35;
        pose.brLegRotX = 0.35;
        break;
      }

      default:
        break;
    }

    return pose;
  }

  // ==========================================
  // II. CHUYỂN ĐỘNG GIA CẦM 2 CHÂN (AVIAN)
  // ==========================================
  computeAvianPose(action, time, _elapsed) {
    const pose = this.createEmptyPose();

    switch (action) {
      case 'idle': {
        // Đứng ngó nghiêng, cánh khép, đuôi nhịp nhẹ
        pose.bodyY = Math.sin(time * 2.0) * 0.005;
        pose.headRotY = Math.sin(time * 1.2) * 0.12;
        pose.headRotX = Math.sin(time * 2.2) * 0.04;
        pose.tailRotZ = Math.sin(time * 2.8) * 0.04;
        pose.leftWingRotZ = -0.04;
        pose.rightWingRotZ = 0.04;
        break;
      }

      case 'walk': {
        // Dáng đi lạch bạch lắc hông, đầu giật về trước theo nhịp chân (Head Bobbing)
        const phase = time * 7.5;
        pose.leftLegRotX = Math.sin(phase) * 0.45;
        pose.rightLegRotX = -Math.sin(phase) * 0.45;

        pose.bodyRotZ = Math.sin(phase) * 0.12;
        pose.bodyY = Math.abs(Math.sin(phase)) * 0.015;
        pose.headPosZ = Math.sin(phase * 2) * 0.02;
        pose.headRotX = Math.sin(phase * 2) * 0.1;
        pose.tailRotZ = Math.sin(phase) * 0.08;
        break;
      }

      case 'run': {
        // Chạy nhanh, thân chúi trước, 2 cánh hơi xòe giữ thăng bằng
        const phase = time * 13.0;
        pose.leftLegRotX = Math.sin(phase) * 0.65;
        pose.rightLegRotX = -Math.sin(phase) * 0.65;

        pose.bodyRotX = 0.12;
        pose.bodyY = Math.abs(Math.sin(phase)) * 0.022;
        pose.leftWingRotZ = -0.28 + Math.sin(phase) * 0.08;
        pose.rightWingRotZ = 0.28 - Math.sin(phase) * 0.08;
        pose.headPosZ = Math.sin(phase * 2) * 0.03;
        pose.headRotX = Math.sin(phase * 2) * 0.12;
        break;
      }

      case 'eat':
      case 'peck': {
        // Mổ thóc nhanh chúc đầu xuống sàn
        const peckPhase = time * 8.5;
        const dip = Math.max(0, Math.sin(peckPhase));
        pose.headRotX = dip * 0.65;
        pose.bodyRotX = dip * 0.12;
        pose.tailRotX = -dip * 0.15;
        break;
      }

      case 'flap': {
        // Đập cánh phành phạch liên tục
        const flapPhase = time * 16.0;
        pose.leftWingRotZ = -0.28 - Math.sin(flapPhase) * 0.65;
        pose.rightWingRotZ = 0.28 + Math.sin(flapPhase) * 0.65;
        pose.bodyY = Math.abs(Math.sin(time * 4.0)) * 0.04;
        pose.headRotX = -0.06;
        break;
      }

      case 'sit': {
        // Ngồi sát sàn theo chiều cao chân
        pose.bodyY = -this.legHeight * 0.65;
        pose.leftLegRotX = -0.5;
        pose.rightLegRotX = -0.5;
        pose.leftWingRotZ = -0.05;
        pose.rightWingRotZ = 0.05;
        break;
      }

      case 'sleep': {
        // Nằm ngủ rúc đầu nhẹ
        pose.bodyY = -this.legHeight * 0.8;
        pose.headRotY = 0.75;
        pose.headRotX = 0.2;
        pose.leftLegRotX = -0.6;
        pose.rightLegRotX = -0.6;
        break;
      }

      default:
        break;
    }

    return pose;
  }

  // ==========================================
  // ÁP DỤNG TƯ THẾ LÊN MESH THREE.JS
  // ==========================================
  applyPose(pose) {
    const p = this.parts;

    // Body
    if (p.body) {
      p.body.position.y = this.baseBodyY + (pose.bodyY || 0);
      p.body.rotation.x = pose.bodyRotX || 0;
      p.body.rotation.y = pose.bodyRotY || 0;
      p.body.rotation.z = pose.bodyRotZ || 0;
    }

    // Neck
    if (p.neck) {
      p.neck.rotation.x = pose.neckRotX || 0;
      p.neck.rotation.y = pose.neckRotY || 0;
      p.neck.rotation.z = pose.neckRotZ || 0;
    }

    // Head
    if (p.head) {
      p.head.rotation.x = pose.headRotX || 0;
      p.head.rotation.y = pose.headRotY || 0;
      p.head.rotation.z = pose.headRotZ || 0;
      if (pose.headPosZ !== undefined && p.baseHeadZ !== undefined) {
        p.head.position.z = p.baseHeadZ + pose.headPosZ;
      }
    }

    // Tail
    if (p.tail) {
      p.tail.rotation.x = pose.tailRotX || 0;
      p.tail.rotation.y = pose.tailRotY || 0;
      p.tail.rotation.z = pose.tailRotZ || 0;
    }

    // Quadruped Legs
    if (p.frontLeftLeg) p.frontLeftLeg.rotation.x = pose.flLegRotX || 0;
    if (p.frontRightLeg) p.frontRightLeg.rotation.x = pose.frLegRotX || 0;
    if (p.backLeftLeg) p.backLeftLeg.rotation.x = pose.blLegRotX || 0;
    if (p.backRightLeg) p.backRightLeg.rotation.x = pose.brLegRotX || 0;

    // Ears
    if (p.leftEar) p.leftEar.rotation.z = pose.leftEarRotZ || 0;
    if (p.rightEar) p.rightEar.rotation.z = pose.rightEarRotZ || 0;

    // Avian Legs & Wings
    if (p.leftLeg) p.leftLeg.rotation.x = pose.leftLegRotX || 0;
    if (p.rightLeg) p.rightLeg.rotation.x = pose.rightLegRotX || 0;
    if (p.leftWing) p.leftWing.rotation.z = pose.leftWingRotZ || 0;
    if (p.rightWing) p.rightWing.rotation.z = pose.rightWingRotZ || 0;
  }
}
